/**
 * Descobrir de qual barbearia é este app.
 *
 * ---
 *
 * QUEM DECIDE É O SERVIDOR, SEMPRE
 *
 * Em produção, o tenant sai do HOST da requisição — `alfa.seudominio.com` ou o
 * domínio próprio da barbearia. O app não escolhe nada: ele pergunta
 * `/api/public/tenant` e recebe a identidade de quem quer que o host seja.
 *
 * O slug daqui serve a UM caso: `localhost`, onde não existe subdomínio sem
 * mexer no arquivo de hosts. A API só aceita o header `x-tenant-slug` FORA de
 * produção, exatamente para que isto não vire um seletor de barbearia na mão de
 * qualquer um.
 *
 * Por isso o app manda o header sempre que souber de um slug: em
 * desenvolvimento ele é a única forma de identificar a barbearia, e em produção
 * é ignorado pelo servidor.
 */

const STORAGE_KEY = 'barber.tenant-slug';

/** `alfa`, `studio-beta` — mesmo padrão aceito pelo servidor. */
const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/;

/** Hosts que nunca carregam slug de barbearia num subdomínio. */
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);

function isValid(slug: string | null | undefined): slug is string {
  return typeof slug === 'string' && SLUG_PATTERN.test(slug);
}

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Navegação privada ou cookies bloqueados: seguir sem memória é melhor do
    // que quebrar o app na primeira linha que ele executa.
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* sem persistência; o slug vale só para esta aba */
  }
}

/**
 * Resolve o slug, na ordem: caminho `/b/:slug` → memória → padrão do app.
 *
 * O caminho vem primeiro porque é uma intenção explícita de quem abriu o link,
 * e é assim que se demonstra o white label numa máquina só: `/b/barbearia-alfa`
 * e `/b/studio-beta` no mesmo `localhost`.
 *
 * O `fallback` é o valor de build de cada app (`VITE_TENANT_SLUG`). Ele entra
 * por PARÂMETRO, e não é lido aqui: `import.meta.env` é do Vite, e amarrar o
 * kit a um bundler específico o tornaria inútil para qualquer outro consumidor
 * — inclusive um teste rodando em Node puro.
 */
export function resolveTenantSlug(
  pathname: string = window.location.pathname,
  fallback?: string | undefined,
): string | null {
  const fromPath = /^\/b\/([^/]+)/.exec(pathname)?.[1]?.toLowerCase();

  if (isValid(fromPath)) {
    write(STORAGE_KEY, fromPath);
    return fromPath;
  }

  const stored = read(STORAGE_KEY);
  if (isValid(stored)) return stored;

  if (isValid(fallback)) return fallback;

  return null;
}

/** O host já identifica a barbearia? Então o slug local é irrelevante. */
export function hostIdentifiesTenant(hostname: string = window.location.hostname): boolean {
  if (LOCAL_HOSTS.has(hostname)) return false;
  return hostname.includes('.');
}

/** Troca a barbearia desta instalação. Usado pela tela de escolha em dev. */
export function setTenantSlug(slug: string): void {
  if (!isValid(slug)) throw new Error(`Slug inválido: "${slug}"`);
  write(STORAGE_KEY, slug);
}

/** Remove o caminho `/b/:slug` da URL, para as rotas do app ficarem limpas. */
export function stripTenantPrefix(pathname: string): string {
  const semPrefixo = pathname.replace(/^\/b\/[^/]+/, '');
  return semPrefixo === '' ? '/' : semPrefixo;
}
